// DocHistoryDrawer.jsx — 文档修改历史抽屉（第五十五批，契约 §5.3）。
//
// 列表与明细分离：列表只取元数据（20 条/页，新→旧），diff 正文按需单取——历史面
// 的全部体量都在 diff 里，一次把整页 diff 发过来等于把翻页的代价前置到每一次开屉。
//
// 三条纪律写在这里，因为它们都是被踩过的坑：
//  1. diff **绝不** dangerouslySetInnerHTML。正文原样携带文档源码，源码里的
//     `<script>` 是合法 markdown 内容；发布管线的反注入不变式（OQ-A14 已裁不得
//     放宽）在这里同样成立。切分在 pages/docHistory.mjs（纯函数、有单测），
//     本组件只把 {type,text} 渲染成受控 <span>。
//  2. 提交者一律显示"工作空间"（OQ-A10 已裁），不暴露 git author。
//  3. 本项目不装 @mui/icons-material，展开标记用文字字形（同 DocBrowserPage 的 caret）。
import { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Drawer, ListItemButton, Stack, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import api from '../api/client.mjs';
import { errKey } from '../api/errText.mjs';
import { fmtTime } from './TicketDetailView.jsx';
import {
  DIFF_ADD, DIFF_CONTEXT, DIFF_DEL, DIFF_HUNK, DIFF_META, PAGE_SIZE,
  cachePageIfAlive, changeKey, diffCache, emptyDrawerState, formatDelta, historyCache,
  historyPageKey, normalizeHistory, pageRange, reconcileDrawerState, splitDiffLines,
} from '../pages/docHistory.mjs';
import { useLang, useT } from '../i18n/index.mjs';

const EMPTY = { enabled: false, total: 0, limit: PAGE_SIZE, offset: 0, entries: [] };

export default function DocHistoryDrawer({ open, group, docKey, docTitle, onClose }) {
  const t = useT();
  const { lang } = useLang();
  const [list, setList] = useState({ status: 'idle', data: EMPTY, error: '' });
  // 重试计数器。"重试"不能写成"把 offset 再设成同一个值"——同值 setState 会被
  // React 直接丢弃，按钮看着能点，其实什么都没发生。
  const [reload, setReload] = useState(0);

  // 换文档（或换组）= 换一份历史：回到第一页，收起展开行。本地 diff 状态清空，
  // 但模块级缓存留着——来回切两篇文档不该重拉。
  //
  // 这三样（offset / openId / diffs）合成**一个**带 scope 的 state，并在渲染期
  // 派生回位，而不是各自一个 useState 加一个 useEffect 去重置：effect 里的
  // setState 对同一次提交里紧接着跑的下一个 effect 不可见，于是下面的加载 effect
  // 会用 (新 docKey, 旧 offset) 打一发谁也没要的请求，还把响应写进那个键。
  // 判据在 pages/docHistory.mjs 的 reconcileDrawerState 单测里。
  const [state, setState] = useState(() => emptyDrawerState(group, docKey));
  const view = reconcileDrawerState(state, group, docKey);
  if (view !== state) setState(view);              // 渲染期纠偏：React 会丢弃本次输出重渲染，effect 只跑一次
  const { offset, openId, diffs } = view;
  // 所有写入都按 scope 对表后再落：await 期间用户可能已经切了文档，那条回来的
  // diff 属于上一篇，不该落进新文档的状态里（它的 openId 也早已作废）。
  const scope = view.scope;
  const patch = useCallback((fn) => {
    setState((s) => (s.scope === scope ? { ...s, ...fn(s) } : s));
  }, [scope]);

  // 列表加载。命中缓存直接落地，不进 loading 态——否则每次开屉都闪一下转圈。
  useEffect(() => {
    if (!open || !group || !docKey) return undefined;
    const key = historyPageKey(group, docKey, offset);
    const hit = historyCache.get(key);
    if (hit) {
      setList({ status: 'ready', data: hit, error: '' });
      return undefined;
    }
    let alive = true;
    setList((s) => ({ ...s, status: 'loading' }));
    (async () => {
      try {
        const raw = await api.docHistory(group, docKey, { limit: PAGE_SIZE, offset });
        const data = normalizeHistory(raw);
        // 写缓存与"这个响应我们还认不认"必须同一道闸：作废的那一发若照写不误，
        // 缓存里就留下一页没人请求过的数据，而且下次会被当成命中直接渲染。
        cachePageIfAlive(historyCache, key, data, alive);
        if (alive) setList({ status: 'ready', data, error: '' });
      } catch (e) {
        if (alive) {
          setList({ status: 'error', data: EMPTY, error: t(errKey(e, { fallback: 'docs.history.error' })) });
        }
      }
    })();
    return () => { alive = false; };
  }, [open, group, docKey, offset, reload, t]);

  const toggleRow = useCallback(async (entry) => {
    const id = entry.changeId;
    if (openId === id) { patch(() => ({ openId: null })); return; }
    patch(() => ({ openId: id }));
    if (diffs[id]?.status === 'ready') return;
    const hit = diffCache.get(changeKey(id));
    if (hit) { patch((s) => ({ diffs: { ...s.diffs, [id]: { status: 'ready', rows: hit } } })); return; }
    patch((s) => ({ diffs: { ...s.diffs, [id]: { status: 'loading' } } }));
    try {
      const data = await api.docChange(group, docKey, id);
      const rows = splitDiffLines(data?.diff);
      diffCache.set(changeKey(id), rows);
      // 按 changeId 落库而不是覆盖一个"当前 diff"：连点两行时，先回来的响应
      // 不会盖掉后点的那行（旧写法就是这么串行的）。
      patch((s) => ({ diffs: { ...s.diffs, [id]: { status: 'ready', rows } } }));
    } catch (e) {
      patch((s) => ({
        diffs: {
          ...s.diffs,
          [id]: { status: 'error', error: t(errKey(e, { fallback: 'docs.history.diffError' })) },
        },
      }));
    }
  }, [openId, diffs, patch, group, docKey, t]);

  const data = list.data || EMPTY;
  const range = pageRange({ total: data.total, offset, limit: data.limit });

  return (
    <Drawer
      anchor="right"
      open={Boolean(open)}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: { xs: '100%', sm: 520, md: 640 }, maxWidth: '100%' } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}
        >
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700 }} variant="subtitle1" noWrap title={docTitle || docKey}>
              {t('docs.history.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {docTitle || docKey}
            </Typography>
          </Box>
          <Button size="small" variant="outlined" onClick={onClose}>{t('common.close')}</Button>
        </Stack>

        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 2, py: 1 }}>
          {list.status === 'loading' ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
          ) : list.status === 'error' ? (
            <Box>
              <Alert severity="error" sx={{ mb: 1 }}>{list.error}</Alert>
              <Button size="small" onClick={() => setReload((n) => n + 1)}>{t('common.retry')}</Button>
            </Box>
          ) : !data.enabled ? (
            // 开关关闭时后端回 200 enabled:false（不是 404）。入口本就不该出现，
            // 走到这里说明开关刚被关/页面拿的是旧的 historyEnabled——说清楚而不是报错。
            <Alert severity="info">{t('docs.history.disabled')}</Alert>
          ) : data.entries.length === 0 ? (
            <Alert severity="info">{t('docs.history.empty')}</Alert>
          ) : (
            <Stack divider={<Divider />} spacing={0}>
              {data.entries.map((e) => (
                <HistoryRow
                  key={e.changeId}
                  entry={e}
                  lang={lang}
                  t={t}
                  expanded={openId === e.changeId}
                  diff={diffs[e.changeId]}
                  onToggle={() => toggleRow(e)}
                />
              ))}
            </Stack>
          )}
        </Box>

        {data.enabled && data.total > 0 ? (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', px: 2, py: 1, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}
          >
            <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
              {t('docs.history.range', { from: range.from, to: range.to, total: data.total })}
            </Typography>
            {/* 倒序翻页：列表是新→旧，所以"更早"是往后翻。 */}
            <Button
              size="small"
              disabled={!range.hasNewer || list.status === 'loading'}
              onClick={() => patch((s) => ({ offset: Math.max(0, s.offset - data.limit) }))}
            >
              {t('docs.history.newer')}
            </Button>
            <Button
              size="small"
              disabled={!range.hasOlder || list.status === 'loading'}
              onClick={() => patch((s) => ({ offset: s.offset + data.limit }))}
            >
              {t('docs.history.older')}
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Drawer>
  );
}

// ── 一行改动 + 展开后的 diff ─────────────────────────────────────────────────
function HistoryRow({ entry, lang, t, expanded, diff, onToggle }) {
  const delta = formatDelta(entry.added, entry.removed);
  return (
    <Box sx={{ py: 1 }}>
      {/* 用 ListItemButton 而不是原生 <button>：行内有 Typography（渲染成 <p>），
          <p> 不是 button 的合法内容，浏览器会就地拆散这棵子树。 */}
      <ListItemButton
        onClick={onToggle}
        aria-expanded={expanded}
        sx={{ display: 'block', textAlign: 'left', borderRadius: 1, px: 1, py: 0.75 }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
          <Box
            component="span"
            aria-hidden
            sx={{
              width: 16, display: 'inline-flex', justifyContent: 'center', fontSize: 11,
              color: 'text.secondary', transition: 'transform 120ms',
              transform: expanded ? 'rotate(90deg)' : 'none',
            }}
          >
            ▶
          </Box>
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {fmtTime(entry.atMs, lang)}
          </Typography>
          <Chip
            size="small"
            variant="outlined"
            label={String(entry.lang || '').toUpperCase()}
            sx={{ height: 18, fontSize: 11 }}
          />
          <Typography variant="body2" sx={{ color: 'success.main', fontFamily: MONO, whiteSpace: 'nowrap' }}>
            {delta.add}
          </Typography>
          <Typography variant="body2" sx={{ color: 'error.main', fontFamily: MONO, whiteSpace: 'nowrap' }}>
            {delta.del}
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO }}>
            {entry.commitRef || ''}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ mt: 0.5, ml: 3, wordBreak: 'break-word' }}>
          {entry.subject || ''}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 3 }}>
          {/* OQ-A10：提交者统一显示"工作空间"，不暴露 git author。 */}
          {t('docs.history.committer')}
          {' · '}
          {entry.eventNo ? t('docs.history.event', { no: entry.eventNo }) : t('docs.history.noEvent')}
        </Typography>
      </ListItemButton>
      {expanded ? (
        <Box sx={{ mt: 1, ml: 3 }}>
          {!diff || diff.status === 'loading' ? (
            <Box sx={{ display: 'flex', py: 2 }}><CircularProgress size={20} /></Box>
          ) : diff.status === 'error' ? (
            <Alert severity="error">{diff.error}</Alert>
          ) : diff.rows.length === 0 ? (
            <Typography variant="caption" color="text.secondary">{t('docs.history.diffEmpty')}</Typography>
          ) : (
            <DiffBody rows={diff.rows} />
          )}
        </Box>
      ) : null}
    </Box>
  );
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

// diff 正文：受控元素，逐行一个 <span>。横向溢出**在这个框里**滚动——
// 页面 body 不得出现横向滚动条（长行的源码是常态，不是例外）。
function DiffBody({ rows }) {
  return (
    <Box
      sx={(theme) => ({
        border: 1, borderColor: 'divider', borderRadius: 1,
        bgcolor: 'action.hover', overflowX: 'auto', maxHeight: 420, overflowY: 'auto',
        fontFamily: MONO, fontSize: 12.5, lineHeight: 1.55,
        '& .dc-diff-line': { display: 'block', px: 1, whiteSpace: 'pre', minWidth: 'max-content' },
        '& .dc-diff-add': {
          bgcolor: alpha(theme.palette.success.main, 0.14), color: theme.palette.success.dark,
        },
        '& .dc-diff-del': {
          bgcolor: alpha(theme.palette.error.main, 0.12), color: theme.palette.error.dark,
        },
        '& .dc-diff-hunk': {
          bgcolor: alpha(theme.palette.info.main, 0.12), color: theme.palette.text.secondary,
        },
        '& .dc-diff-meta': { color: theme.palette.text.disabled },
      })}
    >
      {/* min-width: max-content 让所有行等宽 = 最长那行：否则横向滚动之后，
          短行的 +/− 底色在容器右缘就断掉，看着像高亮只染了一半。 */}
      <Box component="code" sx={{ display: 'block', py: 0.5, minWidth: 'max-content' }}>
        {rows.map((r, i) => (
          <Box
            component="span"
            // 行没有稳定 id，但这份数组在一次展开内是不可变的，下标即身份。
            // eslint-disable-next-line react/no-array-index-key
            key={i}
            className={`dc-diff-line ${LINE_CLASS[r.type] || 'dc-diff-ctx'}`}
          >
            {r.text === '' ? ' ' : r.text}
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const LINE_CLASS = {
  [DIFF_ADD]: 'dc-diff-add',
  [DIFF_DEL]: 'dc-diff-del',
  [DIFF_HUNK]: 'dc-diff-hunk',
  [DIFF_META]: 'dc-diff-meta',
  [DIFF_CONTEXT]: 'dc-diff-ctx',
};
